import { Injectable, signal } from '@angular/core';
import { Toast, ToastInput } from './toast.model';

@Injectable({
  providedIn: 'root',
})
export class ToastService {
  private toastsSignal = signal<Toast[]>([]);

  // Expose as readonly
  readonly toasts = this.toastsSignal.asReadonly();

  /**
   * Show a new toast notification
   * @param toast Toast configuration without id
   */
  show(toast: ToastInput): void {
    const newToast: Toast = {
      ...toast,
      id: `toast-${Date.now()}-${Math.random()}`,
    };

    this.toastsSignal.update(toasts => [...toasts, newToast]);

    // Auto-dismiss after duration
    if (toast.duration) {
      setTimeout(() => {
        this.dismiss(newToast.id);
      }, toast.duration);
    }
  }

  /**
   * Show a success toast
   * @param title Toast title
   * @param message Toast message
   * @param duration Auto-dismiss duration in ms (default: 5000)
   */
  success(title: string, message: string, duration = 5000): void {
    this.show({ type: 'success', title, message, duration });
  }

  /**
   * Show an error toast
   * @param title Toast title
   * @param message Toast message
   * @param duration Auto-dismiss duration in ms (default: 8000)
   */
  error(title: string, message: string, duration = 8000): void {
    this.show({ type: 'error', title, message, duration });
  }

  /**
   * Show a warning toast
   * @param title Toast title
   * @param message Toast message
   * @param duration Auto-dismiss duration in ms (default: 6000)
   */
  warning(title: string, message: string, duration = 6000): void {
    this.show({ type: 'warning', title, message, duration });
  }

  /**
   * Show an info toast
   * @param title Toast title
   * @param message Toast message
   * @param duration Auto-dismiss duration in ms (default: 5000)
   */
  info(title: string, message: string, duration = 5000): void {
    this.show({ type: 'info', title, message, duration });
  }

  /**
   * Dismiss a specific toast by id
   * @param toastId Toast id to dismiss
   */
  dismiss(toastId: string): void {
    this.toastsSignal.update(toasts => toasts.filter(t => t.id !== toastId));
  }

  /**
   * Clear all toasts
   */
  clear(): void {
    this.toastsSignal.set([]);
  }
}
